import React, { useState, useEffect } from 'react';
import { Lock, Rocket, TrendingUp } from 'lucide-react';
import { api } from '../services/api';

/**
 * Overlay that blocks a feature when the business doesn't have enough IRN points.
 * Wraps children with a blur overlay and gamified message.
 */
export default function FeatureGate({ businessId, feature, children, title }) {
  const [eligible, setEligible] = useState(null); // null = loading, true/false = result

  useEffect(() => {
    if (!businessId) { setEligible(true); return; }
    const cached = sessionStorage.getItem(`elig_${businessId}`);
    if (cached) {
      try {
        const data = JSON.parse(cached);
        const rule = data[feature];
        setEligible(rule ? rule.eligible : true);
        return;
      } catch {}
    }
    api.get(`/v2/gamification/business/${businessId}/eligibility`)
      .then(res => {
        const elig = res.data.eligibility || {};
        try { sessionStorage.setItem(`elig_${businessId}`, JSON.stringify(elig)); } catch {}
        const rule = elig[feature];
        setEligible(rule ? rule.eligible : true);
      })
      .catch(() => setEligible(true)); // fail open
  }, [businessId, feature]);

  // Loading or eligible: show children normally
  if (eligible === null || eligible === true) {
    return <>{children}</>;
  }

  // Not eligible: show overlay
  const FEATURE_MESSAGES = {
    promotions: {
      title: 'Promoções',
      message: 'Alcance o nível Perfil Estruturado para publicar promoções e atrair mais clientes.',
      icon: Rocket,
      minLevel: 'Perfil Estruturado (50+ pts)',
    },
    search_trends: {
      title: 'Tendências de Busca',
      message: 'Evolua seu negócio para desbloquear as tendências de busca e entender como seus clientes encontram você.',
      icon: TrendingUp,
      minLevel: 'Perfil Estruturado (50+ pts)',
    },
    analytics_comparativo: {
      title: 'Comparativo com Concorrência',
      message: 'Alcance Negócio em Destaque para comparar seu desempenho com negócios da mesma categoria.',
      icon: TrendingUp,
      minLevel: 'Negócio em Destaque (70+ pts)',
    },
  };

  const info = FEATURE_MESSAGES[feature] || {
    title: title || 'Funcionalidade bloqueada',
    message: 'Evolua seu negócio para desbloquear esta funcionalidade.',
    icon: Lock,
    minLevel: '',
  };
  const Icon = info.icon;

  return (
    <div className="relative" data-testid={`feature-gate-${feature}`}>
      {/* Blurred children */}
      <div className="blur-[3px] opacity-40 pointer-events-none select-none" aria-hidden="true">
        {children}
      </div>
      {/* Overlay */}
      <div className="absolute inset-0 flex items-center justify-center z-10">
        <div className="bg-white/95 backdrop-blur-sm rounded-xl border border-gray-200 shadow-lg px-6 py-5 max-w-sm mx-4 text-center">
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
            <Lock className="w-5 h-5 text-purple-600" />
          </div>
          <h4 className="font-semibold text-sm text-gray-900 mb-1">{info.title}</h4>
          <p className="text-xs text-gray-600 mb-3">{info.message}</p>
          <div className="inline-flex items-center gap-1.5 bg-purple-50 text-purple-700 text-[11px] font-medium px-3 py-1.5 rounded-full">
            <Icon className="w-3.5 h-3.5" />
            Requer: {info.minLevel}
          </div>
        </div>
      </div>
    </div>
  );
}
